<script setup lang="ts">
import { computed, h, ref, watch } from 'vue'

import { Play, Power, PowerOff, Trash2 } from '@lucide/vue'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { type ColumnDef, FlexRender, tableFeatures, useTable } from '@tanstack/vue-table'
import { useRouteParams } from '@vueuse/router'
import { useRouter } from 'vue-router'

import JobForm from '#operations/components/JobForm.vue'
import StatusBadge from '#operations/components/StatusBadge.vue'
import { type JobFormValues, fmtDateUtc, toJobRequest } from '#operations/lib/cron-job'
import DetailState from '#shared/components/DetailState.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import ProductionConfirm from '#shared/components/ProductionConfirm.vue'
import SectionHeading from '#shared/components/SectionHeading.vue'
import TableState from '#shared/components/TableState.vue'
import { chronosClient } from '#shared/services/api/chronos.client'
import type { components } from '#shared/services/api/chronos.schema'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '#ui/alert-dialog'
import { Button } from '#ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#ui/table'
import { ICONS } from '#utils/icons'
import { CONTENT_MOTION, ROW_MOTION } from '#utils/motion'

type JobDto = components['schemas']['JobDto']
type JobRunDto = components['schemas']['JobRunDto']

const ACTIVE_STATUSES = ['Pending', 'Running', 'Waiting']

definePage({
  meta: { requiresAuth: true, title: 'Cron job' },
})

const router = useRouter()
const id = useRouteParams<string>('jobId')

const RUNS_PAGE_SIZE = 15
const runsLimit = ref(RUNS_PAGE_SIZE)
watch(id, () => {
  runsLimit.value = RUNS_PAGE_SIZE
})

const queryClient = useQueryClient()

const jobKey = computed(() => ['cron-job', id.value] as const)

const jobQuery = useQuery({
  queryKey: jobKey,
  queryFn: async () => {
    const { data } = await chronosClient.GET('/api/admin/jobs/{id}', {
      params: { path: { id: id.value } },
    })
    return data ?? null
  },
})

const runsQuery = useQuery({
  queryKey: computed(() => ['cron-job-runs', id.value, runsLimit.value] as const),
  queryFn: async (): Promise<JobRunDto[]> => {
    const { data } = await chronosClient.GET('/api/admin/jobs/{id}/runs', {
      params: { path: { id: id.value }, query: { limit: runsLimit.value + 1 } },
    })
    return data ?? []
  },
  placeholderData: keepPreviousData,
  refetchInterval: (query) => {
    const runs = query.state.data as JobRunDto[] | undefined
    return runs?.some((r) => r.status && ACTIVE_STATUSES.includes(r.status)) ? 5000 : false
  },
})

const visibleRuns = computed(() => (runsQuery.data.value ?? []).slice(0, runsLimit.value))
const canLoadMore = computed(
  () => runsQuery.isPlaceholderData.value || (runsQuery.data.value?.length ?? 0) > runsLimit.value,
)

function loadMore() {
  runsLimit.value += RUNS_PAGE_SIZE
}

const updateJob = useMutation({
  mutationFn: (values: JobFormValues) =>
    chronosClient.PUT('/api/admin/jobs/{id}', {
      params: { path: { id: id.value } },
      body: toJobRequest(values),
    }),
  meta: { success: 'Job saved.', error: 'Failed to save job.' },
})

const setEnabled = useMutation({
  mutationFn: (enabled: boolean) =>
    enabled
      ? chronosClient.POST('/api/admin/jobs/{id}/enable', { params: { path: { id: id.value } } })
      : chronosClient.POST('/api/admin/jobs/{id}/disable', { params: { path: { id: id.value } } }),
  onMutate: async (enabled: boolean) => {
    await queryClient.cancelQueries({ queryKey: jobKey.value })
    const previous = queryClient.getQueryData<JobDto>(jobKey.value)
    if (previous)
      queryClient.setQueryData<JobDto>(jobKey.value, { ...previous, isEnabled: enabled })
    return { previous }
  },
  onError: (_error, _enabled, context) => {
    if (context?.previous) queryClient.setQueryData(jobKey.value, context.previous)
  },
  meta: { error: 'Failed to update status.' },
})

const triggerJob = useMutation({
  mutationFn: () =>
    chronosClient.POST('/api/admin/jobs/{id}/trigger', { params: { path: { id: id.value } } }),
  meta: {
    success: 'Job triggered — the new run will appear in the history below.',
    error: 'Failed to trigger job.',
  },
})

const deleteJob = useMutation({
  mutationFn: () =>
    chronosClient.DELETE('/api/admin/jobs/{id}', { params: { path: { id: id.value } } }),
})

const toggleConfirmOpen = ref(false)
const triggerConfirmOpen = ref(false)
const deleteConfirmOpen = ref(false)

const initial = computed<Partial<JobFormValues> | undefined>(() => {
  const j = jobQuery.data.value
  if (!j) return undefined
  return {
    name: j.name ?? '',
    description: j.description ?? '',
    targetService: j.targetService ?? '',
    endpointPath: j.endpointPath ?? '',
    httpMethod: j.httpMethod,
    cronExpression: j.cronExpression ?? '',
    timeoutSeconds: j.timeoutSeconds ?? null,
    requestBody: j.requestBody ?? '',
    isEnabled: j.isEnabled,
  }
})

function handleSubmit(values: JobFormValues) {
  updateJob.mutate(values)
}

function handleToggle() {
  setEnabled.mutate(!jobQuery.data.value?.isEnabled, {
    onSettled: () => (toggleConfirmOpen.value = false),
  })
}

function handleTrigger() {
  triggerJob.mutate(undefined, { onSettled: () => (triggerConfirmOpen.value = false) })
}

function handleDelete() {
  deleteJob.mutate(undefined, {
    onSuccess: () => {
      deleteConfirmOpen.value = false
      router.push('/cron')
    },
  })
}

const features = tableFeatures({})

const runColumns: ColumnDef<typeof features, JobRunDto>[] = [
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.status
        ? h(StatusBadge, { status: row.original.status })
        : h('span', { class: 'text-sm text-muted-foreground' }, '—'),
  },
  {
    accessorKey: 'triggerType',
    header: 'Trigger',
    cell: ({ row }) =>
      h('span', { class: 'text-sm text-muted-foreground' }, row.original.triggerType),
  },
  {
    accessorKey: 'attempt',
    header: 'Attempt',
    cell: ({ row }) => h('span', { class: 'text-sm' }, row.original.attempt?.toString() ?? '—'),
  },
  {
    accessorKey: 'startedAtUtc',
    header: 'Started',
    cell: ({ row }) => h('span', { class: 'text-sm' }, fmtDateUtc(row.original.startedAtUtc)),
  },
  {
    accessorKey: 'completedAtUtc',
    header: 'Completed',
    cell: ({ row }) => h('span', { class: 'text-sm' }, fmtDateUtc(row.original.completedAtUtc)),
  },
  {
    accessorKey: 'httpStatusCode',
    header: 'HTTP',
    cell: ({ row }) =>
      h('span', { class: 'font-mono text-sm' }, row.original.httpStatusCode?.toString() ?? '—'),
  },
  {
    accessorKey: 'error',
    header: 'Error',
    cell: ({ row }) =>
      row.original.error
        ? h(
            'span',
            {
              class: 'block max-w-xs truncate text-sm text-destructive',
              title: row.original.error,
            },
            row.original.error,
          )
        : h('span', { class: 'text-sm text-muted-foreground' }, '—'),
  },
]

const runsTable = useTable({
  features,
  data: computed(() => visibleRuns.value),
  columns: runColumns,
})

const title = computed(() => jobQuery.data.value?.name || 'Job')
const isEnabled = computed(() => jobQuery.data.value?.isEnabled ?? false)
const deleteConfirmed = ref(true)
</script>

<template>
  <PageHeader :title="title">
    <template #actions>
      <AlertDialog v-model:open="toggleConfirmOpen">
        <AlertDialogTrigger as-child>
          <Button
            variant="outline"
            :disabled="jobQuery.isLoading.value || setEnabled.isPending.value"
          >
            <component :is="isEnabled ? PowerOff : Power" />
            {{ isEnabled ? 'Disable' : 'Enable' }}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {{ isEnabled ? 'Disable this job?' : 'Enable this job?' }}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {{
                isEnabled
                  ? 'The job will stop running on its schedule until it is re-enabled.'
                  : 'The job will start running automatically on its cron schedule.'
              }}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction :disabled="setEnabled.isPending.value" @click="handleToggle">
              {{ isEnabled ? 'Disable' : 'Enable' }}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog v-model:open="triggerConfirmOpen">
        <AlertDialogTrigger as-child>
          <Button
            variant="outline"
            :disabled="jobQuery.isLoading.value || triggerJob.isPending.value"
          >
            <Play />
            {{ triggerJob.isPending.value ? 'Triggering…' : 'Trigger now' }}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Trigger this job now?</AlertDialogTitle>
            <AlertDialogDescription>
              This runs the job once immediately, in addition to its normal schedule. It will call
              <span class="font-mono">
                {{ jobQuery.data.value?.httpMethod }}
                {{ jobQuery.data.value?.endpointPath }} </span
              >{{
                jobQuery.data.value?.targetService
                  ? ` on ${jobQuery.data.value.targetService}`
                  : ''
              }}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction :disabled="triggerJob.isPending.value" @click="handleTrigger">
              Trigger now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog v-model:open="deleteConfirmOpen">
        <AlertDialogTrigger as-child>
          <Button variant="destructive" :disabled="jobQuery.isLoading.value">
            <Trash2 />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the scheduled job. It will no longer run. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ProductionConfirm v-model:confirmed="deleteConfirmed" phrase="delete job" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              :disabled="deleteJob.isPending.value || !deleteConfirmed"
              @click="handleDelete"
            >
              {{ deleteJob.isPending.value ? 'Deleting…' : 'Delete' }}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </template>
  </PageHeader>

  <Transition v-bind="CONTENT_MOTION">
    <DetailState
      v-if="jobQuery.isLoading.value || jobQuery.error.value"
      :is-loading="jobQuery.isLoading.value"
      :error="jobQuery.error.value"
      noun="job"
    />

    <div v-else-if="jobQuery.data.value" class="flex flex-col gap-6">
      <dl class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div class="flex flex-col">
          <dt class="text-xs text-muted-foreground">Next run</dt>
          <dd>{{ fmtDateUtc(jobQuery.data.value.nextFireAtUtc) }}</dd>
        </div>
        <div class="flex flex-col">
          <dt class="text-xs text-muted-foreground">Last run</dt>
          <dd>{{ fmtDateUtc(jobQuery.data.value.lastRunAtUtc) }}</dd>
        </div>
        <div class="flex flex-col">
          <dt class="text-xs text-muted-foreground">Created</dt>
          <dd>{{ fmtDateUtc(jobQuery.data.value.createdAtUtc) }}</dd>
        </div>
        <div class="flex flex-col">
          <dt class="text-xs text-muted-foreground">Updated</dt>
          <dd>{{ fmtDateUtc(jobQuery.data.value.updatedAtUtc) }}</dd>
        </div>
      </dl>

      <JobForm
        :initial="initial"
        :submitting="updateJob.isPending.value"
        submit-label="Save"
        @submit="handleSubmit"
        @cancel="router.push('/cron')"
      />

      <section class="flex flex-col gap-3">
        <SectionHeading title="Run history" description="The most recent runs of this job." />
        <div class="-mx-3">
          <Table>
            <TableHeader>
              <TableRow v-for="headerGroup in runsTable.getHeaderGroups()" :key="headerGroup.id">
                <TableHead v-for="header in headerGroup.headers" :key="header.id">
                  <FlexRender
                    v-if="!header.isPlaceholder"
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableState
                :colspan="runColumns.length"
                :is-loading="runsQuery.isLoading.value"
                :is-empty="!runsTable.getRowModel().rows.length"
                noun="runs"
                :icon="ICONS.jobRuns"
              >
                <template #description>
                  Runs appear here once the job fires — on its schedule, or when you trigger it.
                </template>
              </TableState>
              <TransitionGroup v-bind="ROW_MOTION">
                <TableRow v-for="row in runsTable.getRowModel().rows" :key="row.id">
                  <TableCell v-for="cell in row.getAllCells()" :key="cell.id">
                    <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                  </TableCell>
                </TableRow>
              </TransitionGroup>
            </TableBody>
          </Table>
        </div>
        <div v-if="canLoadMore" class="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            :disabled="runsQuery.isPlaceholderData.value"
            @click="loadMore"
          >
            {{ runsQuery.isPlaceholderData.value ? 'Loading…' : 'Load more' }}
          </Button>
        </div>
      </section>
    </div>
  </Transition>
</template>
